import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../api/api";

const LoginPage = () => {
    const { register, handleSubmit } = useForm();
    const [serverError, setServerError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();

    const onSubmit = async (form) => {
        if (loading) return;

        setLoading(true);
        setServerError("");

        try {
            const loginValue = form.username?.trim();
            const password = form.password?.trim();

            if (!loginValue || !password) {
                throw new Error("Username/email and password are required");
            }

            const payload = loginValue.includes("@")
                ? { email: loginValue, password }
                : { username: loginValue, password };

            const { data: result } = await authApi.post("/login/authenticate", payload);

            if (result?.requiresTwoFactor === true || result?.requires2FA === true) {
                if (!result.userId) {
                    throw new Error("2FA required but userId not provided");
                }

                sessionStorage.setItem("2fa:userId", String(result.userId));
                navigate("/2fa/verify-login");
                return;
            }

            if (!result?.token) {
                throw new Error("Token was not returned by the server");
            }

            login(result.token);

            try {
                const decoded = jwtDecode(result.token);

                const role =
                    decoded.role ||
                    decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ||
                    decoded.roles;

                if (role) {
                    localStorage.setItem("role", String(role).toLowerCase());
                }
            } catch {
                console.warn("JWT decode failed");
            }

            navigate("/dashboard");
        } catch (err) {
            console.error("Login error:", err);

            const data = err?.response?.data;

            if (
                err?.response?.status === 401 &&
                data?.requiresTwoFactor === true &&
                data?.userId
            ) {
                sessionStorage.setItem("2fa:userId", String(data.userId));
                navigate("/2fa/verify-login");
                return;
            }

            if (err?.response?.status === 401) {
                setServerError(data?.message || "Invalid credentials or account locked");
            } else {
                setServerError(
                    data?.message ||
                    data ||
                    err?.message ||
                    "Authorization error"
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-8">
                <h2 className="text-3xl font-bold mb-2 text-center">
                    Login
                </h2>

                <p className="text-slate-600 text-center mb-6">
                    Sign in to IDLogicChain platform.
                </p>

                {serverError && (
                    <div className="mb-5 p-3 rounded-lg bg-red-100 text-red-700 text-center">
                        {serverError}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Email or Username
                        </label>

                        <input
                            {...register("username", { required: true })}
                            className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                            autoComplete="username"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Password
                        </label>

                        <input
                            type="password"
                            {...register("password", { required: true })}
                            className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                            autoComplete="current-password"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition disabled:opacity-60"
                    >
                        {loading ? "Logging in..." : "Login"}
                    </button>
                </form>

                <div className="grid grid-cols-1 gap-3 mt-6">
                    <Link
                        to="/register"
                        className="w-full text-center bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-3 rounded-xl transition"
                    >
                        Register
                    </Link>

                    <Link
                        to="/forgot-password"
                        className="w-full text-center bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-3 rounded-xl transition"
                    >
                        Forgot Password
                    </Link>

                    <Link
                        to="/feedback"
                        className="w-full text-center bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-3 rounded-xl transition"
                    >
                        Send Feedback
                    </Link>

                    <Link
                        to="/guest"
                        className="w-full text-center bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition"
                    >
                        🚀 Explore Platform
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;